import React, { useState, useCallback } from 'react';
import {
    Box, Typography, Button, Card, CardContent, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Alert, Chip, IconButton, Tooltip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DraftsIcon from '@mui/icons-material/Drafts';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useMsal } from '@azure/msal-react';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';
import { saveHtmlDraft } from '../../utils/graphMailService';
import PageHeader from '../../ui-components/PageHeader';

function SummaryCard({ label, value, color = '#333', icon }) {
    return (
        <Card sx={{ flex: '1 1 160px', minWidth: 140 }}>
            <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>{icon}</Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
                <Typography variant="caption" sx={{ color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {label}
                </Typography>
            </CardContent>
        </Card>
    );
}

const statusConfig = {
    ok: { label: 'Op schema', color: '#28a745', bgColor: '#d1e7dd', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
    low: { label: 'Achterstallig', color: '#f59e0b', bgColor: '#fff3cd', icon: <WarningIcon sx={{ fontSize: 16 }} /> },
    missing: { label: 'Geen uren', color: '#dc3545', bgColor: '#f8d7da', icon: <ErrorIcon sx={{ fontSize: 16 }} /> },
    error: { label: 'Fout', color: '#6c757d', bgColor: '#e2e3e5', icon: <ErrorIcon sx={{ fontSize: 16 }} /> },
};

export default function UrenCheck() {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [weeksAgo, setWeeksAgo] = useState(1);
    const [draftStatus, setDraftStatus] = useState(null); // null | 'loading' | 'done' | 'error'
    const [draftCount, setDraftCount] = useState(0);

    const checkWeek = useCallback(async (weeks = weeksAgo) => {
        setLoading(true);
        setError(null);
        setDraftStatus(null);
        try {
            const res = await authFetch(`${BACKEND_URL}/Api/uren/check-week`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ weeksAgo: weeks }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Fout ${res.status}`);
            }
            setData(await res.json());
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [weeksAgo]);

    const createDrafts = useCallback(async (type) => {
        if (!data || !activeAccount) return;

        const missing = data.missing || [];
        if (missing.length === 0 && type === 'reminders') {
            setError('Geen achterstallige medewerkers gevonden');
            return;
        }

        setDraftStatus('loading');
        try {
            // Get email HTML from backend
            const res = await authFetch(`${BACKEND_URL}/Api/uren/reminder-emails`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ employees: missing, week: data.week }),
            });
            if (!res.ok) throw new Error('Kon emails niet genereren');
            const emailData = await res.json();

            let created = 0;

            if (type === 'reminders' || type === 'all') {
                // Create individual reminder drafts
                for (const reminder of emailData.reminders) {
                    await saveHtmlDraft(instance, activeAccount, {
                        subject: reminder.subject,
                        html: reminder.html,
                        to: reminder.to,
                    });
                    created++;
                }
            }

            if (type === 'summary' || type === 'all') {
                // Create management summary draft
                const summary = emailData.summaryEmail;
                await saveHtmlDraft(instance, activeAccount, {
                    subject: summary.subject,
                    html: summary.html,
                    to: summary.to,
                    cc: summary.cc,
                });
                created++;
            }

            setDraftCount(created);
            setDraftStatus('done');
        } catch (err) {
            setError(err.message);
            setDraftStatus('error');
        }
    }, [data, activeAccount, instance]);

    const handleWeekChange = (delta) => {
        const newWeeks = Math.max(0, weeksAgo + delta);
        setWeeksAgo(newWeeks);
        checkWeek(newWeeks);
    };

    const weekLabel = data?.week
        ? `Week ${data.week.weekNum} (${data.week.start} t/m ${data.week.end})`
        : weeksAgo === 0 ? 'Huidige week' : weeksAgo === 1 ? 'Vorige week' : `${weeksAgo} weken geleden`;

    return (
        <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
            <PageHeader
                icon="schedule"
                title="Uren Controle"
                subtitle="Check of medewerkers hun uren in Gripp hebben geschreven"
            />

            {/* Week selector + check button */}
            <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Week eerder"><IconButton size="small" onClick={() => handleWeekChange(1)}><NavigateBeforeIcon /></IconButton></Tooltip>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: 250, textAlign: 'center' }}>
                        {weekLabel}
                    </Typography>
                    <Tooltip title="Week later"><IconButton size="small" onClick={() => handleWeekChange(-1)} disabled={weeksAgo <= 0}><NavigateNextIcon /></IconButton></Tooltip>
                </Box>
                <Button
                    variant="contained" startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                    onClick={() => checkWeek()} disabled={loading}
                >
                    {loading ? 'Checken...' : 'Check uren'}
                </Button>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {data?.warning && <Alert severity="warning" sx={{ mb: 2 }}>{data.warning}</Alert>}

            {draftStatus === 'done' && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDraftStatus(null)}>
                    {draftCount} email concept{draftCount !== 1 ? 'en' : ''} aangemaakt in Outlook. Check je Concepten map.
                </Alert>
            )}

            {data && (
                <>
                    {/* Summary cards */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                        <SummaryCard
                            label="Medewerkers" value={data.summary.total} color="#0d6efd"
                            icon={<CheckCircleIcon sx={{ color: '#0d6efd' }} />}
                        />
                        <SummaryCard
                            label="Op schema" value={data.summary.on_track} color="#28a745"
                            icon={<CheckCircleIcon sx={{ color: '#28a745' }} />}
                        />
                        <SummaryCard
                            label="Achterstallig" value={data.summary.missing} color="#dc3545"
                            icon={<WarningIcon sx={{ color: '#dc3545' }} />}
                        />
                    </Box>

                    {/* Action buttons */}
                    {data.summary.missing > 0 && (
                        <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', bgcolor: '#fef3c7' }}>
                            <Button
                                variant="contained" color="warning" startIcon={<DraftsIcon />}
                                onClick={() => createDrafts('reminders')}
                                disabled={draftStatus === 'loading'}
                            >
                                Maak reminder mails ({data.summary.missing})
                            </Button>
                            <Button
                                variant="outlined" color="warning" startIcon={<SummarizeIcon />}
                                onClick={() => createDrafts('summary')}
                                disabled={draftStatus === 'loading'}
                            >
                                Management samenvatting
                            </Button>
                            <Button
                                variant="contained" startIcon={draftStatus === 'loading' ? <CircularProgress size={18} color="inherit" /> : <DraftsIcon />}
                                onClick={() => createDrafts('all')}
                                disabled={draftStatus === 'loading'}
                            >
                                {draftStatus === 'loading' ? 'Bezig...' : 'Alles aanmaken'}
                            </Button>
                        </Paper>
                    )}

                    {/* Employee table */}
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Medewerker</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Contract</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Geschreven</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">%</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.employees.map((emp) => {
                                    const cfg = statusConfig[emp.status] || statusConfig.error;
                                    return (
                                        <TableRow key={emp.email} hover>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{emp.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">{emp.email}</Typography>
                                            </TableCell>
                                            <TableCell align="center">{emp.contract_hours}u</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600, color: cfg.color }}>
                                                {emp.written_hours}u
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: cfg.color }}>
                                                    {emp.percentage}%
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    icon={cfg.icon}
                                                    label={cfg.label}
                                                    size="small"
                                                    sx={{ bgcolor: cfg.bgColor, color: cfg.color, fontWeight: 500, fontSize: 12 }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
}
